import React, {
  createContext,
  useReducer,
  useContext,
  useEffect,
  useCallback,
  useRef,
} from "react";
import terminalService from "../services/terminalService";
import { terminalAPI } from "../services/api";
import { generateSmartDescription } from "../utils/posUtils";

// Issue #289 — the card payment currently open on the reader for this POS
// screen, and the watcher of its status. Shared by the cart (which starts
// a payment) and the payment modal (which can cancel it), so it lives at
// module level: there is one POS screen per browser tab.
//   openPaymentIntentId: an unpaid attempt. The next attempt asks the
//     server to cancel it first, so its card hold cannot stay open.
// `starting`: the server call that starts a payment, while it runs. A
// "Cancel payment" pressed then waits for it (the payment has no ID yet)
// and sets `cancelRequested`, so the start does not go on to watch it.
export const posPaymentSession = {
  openPaymentIntentId: null,
  watcher: null,
  lastPaymentArgs: null,
  starting: null,
  cancelRequested: false,
};

// A staff "Cancel payment" (no charge) is shown differently from a decline.
export const STAFF_CANCELLED = "STAFF_CANCELLED";

const stopPaymentWatcher = () => {
  if (posPaymentSession.watcher) {
    posPaymentSession.watcher.stop();
    posPaymentSession.watcher = null;
  }
};

// Action Types
const CART_ADD_ITEM = "CART_ADD_ITEM";
const CART_UPDATE_QUANTITY = "CART_UPDATE_QUANTITY";
const CART_REMOVE_ITEM = "CART_REMOVE_ITEM";
const CART_CLEAR = "CART_CLEAR";
const CART_UPDATE_ITEMS = "CART_UPDATE_ITEMS";
const CART_APPLY_DISCOUNT = "CART_APPLY_DISCOUNT";
const CART_REMOVE_DISCOUNT = "CART_REMOVE_DISCOUNT";
const CART_SET_CUSTOMER = "CART_SET_CUSTOMER";
const CART_CLEAR_CUSTOMER = "CART_CLEAR_CUSTOMER";

const TERMINAL_SET_READERS = "TERMINAL_SET_READERS";
const TERMINAL_SELECT_READER = "TERMINAL_SELECT_READER";
const TERMINAL_SET_LOCATIONS = "TERMINAL_SET_LOCATIONS";
const TERMINAL_SELECT_LOCATION = "TERMINAL_SELECT_LOCATION";
const TERMINAL_SET_DISPLAYING = "TERMINAL_SET_DISPLAYING";
const TERMINAL_ERROR = "TERMINAL_ERROR";
const TERMINAL_CLEAR_ERROR = "TERMINAL_CLEAR_ERROR";

const PAYMENT_START = "PAYMENT_START";
const PAYMENT_SUCCESS = "PAYMENT_SUCCESS";
const PAYMENT_FAILURE = "PAYMENT_FAILURE";
const PAYMENT_RESET = "PAYMENT_RESET";
const PAYMENT_SET_DATA = "PAYMENT_SET_DATA";
const PAYMENT_UPDATE_DATA = "PAYMENT_UPDATE_DATA";
const PAYMENT_ERROR = "PAYMENT_ERROR";
// Issue #289: the server's error code (e.g. POS_READER_BUSY), so the
// payment screen can offer "Clear reader".
const PAYMENT_ERROR_CODE = "PAYMENT_ERROR_CODE";

const normalizePOSProductType = (value) => {
  const rawValue = (value || "").toString().toLowerCase();

  if (!rawValue) return null;
  if (rawValue.includes("membership")) {
    if (rawValue.includes("sauna")) return "sauna-membership";
    if (rawValue.includes("red")) return "red-light-membership";
    if (rawValue.includes("combo")) return "combo-membership";
    return "membership";
  }
  if (rawValue.includes("package")) {
    if (rawValue.includes("sauna")) return "sauna-package";
    if (rawValue.includes("red")) return "red-light-package";
    return "package";
  }
  if (rawValue.includes("drop")) {
    if (rawValue.includes("sauna")) return "drop-in-sauna";
    if (rawValue.includes("red")) return "drop-in-red-light";
    return "drop-in";
  }
  if (rawValue.includes("merch")) return "merch";

  return "other";
};

// Initial State
const initialState = {
  cart: {
    items: [],
    totals: {
      subtotal: 0,
      discountAmount: 0,
      taxableAmount: 0,
      taxAmount: 0,
      total: 0,
    },
    appliedDiscount: null,
    customer: null,
  },
  terminal: {
    readers: [],
    selectedReader: null,
    locations: [],
    selectedLocation: null,
    isDisplaying: false,
    error: null,
    lastDisplayTime: null,
  },
  payment: {
    step: null, // null, 'processing', 'success', 'failed'
    paymentData: null,
    error: null,
    errorCode: null,
    lastOrderCompleted: false,
  },
};

// Reducers
const cartReducer = (state, action) => {
  switch (action.type) {
    case CART_ADD_ITEM: {
      const product = action.payload;

      // Check if item already exists in cart with the same price
      const existingItemIndex = state.items.findIndex(
        (item) =>
          item.productId === product.id &&
          item.unitPrice === product.price &&
          item.priceId ===
            (product.selectedPriceId || product.default_price?.id)
      );

      let updatedItems;
      if (existingItemIndex >= 0) {
        // Update quantity if item exists with same price
        updatedItems = [...state.items];
        updatedItems[existingItemIndex].quantity += 1;
        updatedItems[existingItemIndex].totalPrice =
          updatedItems[existingItemIndex].quantity *
          updatedItems[existingItemIndex].unitPrice;
      } else {
        // Find price details for display
        let priceLabel = "";
        let priceId = product.default_price?.id;

        if (product.selectedPriceId && product.prices) {
          const selectedPrice = product.prices.find(
            (p) => p.id === product.selectedPriceId
          );
          if (selectedPrice) {
            priceId = selectedPrice.id;
            if (selectedPrice.recurring) {
              priceLabel = `${selectedPrice.recurring.interval_count} ${selectedPrice.recurring.interval}`;
            }
          }
        }

        const rawProductType =
          product.metadata?.product_type ||
          product.metadata?.productType ||
          product.metadata?.type ||
          product.metadata?.creditCategory ||
          product.category;

        // Add new item if it doesn't exist or has different price
        const newItem = {
          productId: product.id,
          name: product.name,
          unitPrice: product.price,
          quantity: 1,
          totalPrice: product.price,
          category: product.category,
          taxCode: product.tax_code,
          sku: product.sku || "",
          image: product.image || null,
          priceId: priceId,
          priceLabel: priceLabel,
          // Preserve any product type information from the product
          productType: normalizePOSProductType(rawProductType),
          metadata: product.metadata || {},
        };

        // Product metadata handling

        updatedItems = [...state.items, newItem];
      }

      // Calculate new totals
      const newTotals = calculateTotals(updatedItems, state.appliedDiscount);

      return {
        ...state,
        items: updatedItems,
        totals: newTotals,
      };
    }

    case CART_UPDATE_QUANTITY: {
      const { productId, quantity } = action.payload;
      const updatedItems = state.items.map((item) => {
        if (item.productId === productId) {
          const newQuantity = Math.max(1, quantity); // Ensure quantity is at least 1
          return {
            ...item,
            quantity: newQuantity,
            totalPrice: newQuantity * item.unitPrice,
          };
        }
        return item;
      });

      // Calculate new totals
      const newTotals = calculateTotals(updatedItems, state.appliedDiscount);

      return {
        ...state,
        items: updatedItems,
        totals: newTotals,
      };
    }

    case CART_REMOVE_ITEM: {
      const productId = action.payload;
      const updatedItems = state.items.filter(
        (item) => item.productId !== productId
      );

      // Calculate new totals
      const newTotals = calculateTotals(updatedItems, state.appliedDiscount);

      return {
        ...state,
        items: updatedItems,
        totals: newTotals,
      };
    }

    case CART_CLEAR:
      return {
        ...state,
        items: [],
        totals: {
          subtotal: 0,
          discountAmount: 0,
          taxableAmount: 0,
          taxAmount: 0,
          total: 0,
        },
        appliedDiscount: null,
      };

    case CART_UPDATE_ITEMS: {
      const updatedItems = action.payload;

      // Calculate new totals
      const newTotals = calculateTotals(updatedItems, state.appliedDiscount);

      return {
        ...state,
        items: updatedItems,
        totals: newTotals,
      };
    }

    case CART_APPLY_DISCOUNT: {
      const discount = action.payload;

      // Calculate new totals with discount
      const newTotals = calculateTotals(state.items, discount);

      return {
        ...state,
        appliedDiscount: discount,
        totals: newTotals,
      };
    }

    case CART_REMOVE_DISCOUNT: {
      // Calculate new totals without discount
      const newTotals = calculateTotals(state.items, null);

      return {
        ...state,
        appliedDiscount: null,
        totals: newTotals,
      };
    }

    case CART_SET_CUSTOMER:
      return {
        ...state,
        customer: action.payload,
      };

    case CART_CLEAR_CUSTOMER:
      return {
        ...state,
        customer: null,
      };

    default:
      return state;
  }
};

const terminalReducer = (state, action) => {
  switch (action.type) {
    case TERMINAL_SET_READERS:
      return {
        ...state,
        readers: action.payload,
      };

    case TERMINAL_SELECT_READER:
      return {
        ...state,
        selectedReader: action.payload,
        error: null, // Clear any previous errors when selecting a new reader
      };

    case TERMINAL_SET_LOCATIONS:
      return {
        ...state,
        locations: action.payload,
      };

    case TERMINAL_SELECT_LOCATION:
      return {
        ...state,
        selectedLocation: action.payload,
        selectedReader: null, // Reset selected reader when changing location
        error: null,
      };

    case TERMINAL_SET_DISPLAYING:
      return {
        ...state,
        isDisplaying: action.payload,
        lastDisplayTime: action.payload
          ? new Date().getTime()
          : state.lastDisplayTime,
      };

    case TERMINAL_ERROR:
      return {
        ...state,
        error: action.payload,
      };

    case TERMINAL_CLEAR_ERROR:
      return {
        ...state,
        error: null,
      };

    default:
      return state;
  }
};

const paymentReducer = (state, action) => {
  switch (action.type) {
    case PAYMENT_START:
      return {
        ...state,
        step: "processing",
        error: null,
        errorCode: null,
        lastOrderCompleted: false,
      };

    case PAYMENT_SUCCESS:
      return {
        ...state,
        step: "success",
        error: null,
        lastOrderCompleted: true,
      };

    case PAYMENT_FAILURE:
      return {
        ...state,
        step: "failed",
        error: action.payload,
        lastOrderCompleted: false,
      };

    case PAYMENT_RESET:
      return {
        ...state,
        step: null,
        paymentData: null,
        error: null,
        errorCode: null,
        lastOrderCompleted: false,
      };

    case PAYMENT_ERROR_CODE:
      return {
        ...state,
        errorCode: action.payload || null,
      };

    case PAYMENT_SET_DATA:
      return {
        ...state,
        paymentData: action.payload,
      };

    case PAYMENT_UPDATE_DATA:
      return {
        ...state,
        paymentData: {
          ...state.paymentData,
          ...action.payload,
        },
      };

    case PAYMENT_ERROR:
      return {
        ...state,
        error: action.payload,
      };

    default:
      return state;
  }
};

// Helper function to calculate cart totals
const calculateTotals = (items, discount) => {
  // Calculate subtotal from original prices
  const subtotal = items.reduce(
    (sum, item) => sum + item.unitPrice * item.quantity,
    0
  );

  // Get discount amount
  const discountAmount = discount ? discount.amount : 0;

  // Calculate taxable amount
  const taxableAmount = Math.max(0, subtotal - discountAmount);

  // Calculate tax (8.6%)
  const taxRate = 0.086;
  const taxAmount = Math.round(taxableAmount * taxRate);

  return {
    subtotal,
    discountAmount,
    taxableAmount,
    taxAmount,
    total: taxableAmount + taxAmount,
  };
};

// Root reducer that combines all reducers
const rootReducer = (state, action) => {
  // Handle the FORCE_UPDATE action type
  if (action.type === "FORCE_UPDATE") {
    // Just return the current state to trigger a re-render
    return { ...state };
  }

  return {
    cart: cartReducer(state.cart, action),
    terminal: terminalReducer(state.terminal, action),
    payment: paymentReducer(state.payment, action),
  };
};

// Create context
const POSContext = createContext();

// Issue #289: a cart display in flight clears the reader and then shows
// the cart (about 300 ms later on the server). If a payment starts inside
// that window, the display knocks the payment off the reader. The payment
// waits for any display in flight to finish first.
export const posDisplayInFlight = { promise: null };
const trackDisplay = (promise) => {
  posDisplayInFlight.promise = promise;
  promise.finally(() => {
    if (posDisplayInFlight.promise === promise) posDisplayInFlight.promise = null;
  }).catch(() => {});
  return promise;
};

// Whether this screen has a cart on the reader that nothing has cleared.
// The reader keeps the last cart until it is cleared.
export const posReaderCart = { shown: false };

// Clears the cart from the reader, tracked like a display (a payment from
// this screen waits for it). Never throws.
const clearReaderCart = (readerId) => {
  posReaderCart.shown = false;
  // A cart display still in flight would land after the clear and show
  // the cart again, so the clear waits for it.
  const displayBefore = posDisplayInFlight.promise;
  return trackDisplay(
    Promise.resolve(displayBefore)
      .catch(() => {})
      .then(() => terminalService.clearDisplay(readerId))
  ).catch((error) => ({ success: false, error: error.message }));
};

// Provider component
export const POSProvider = ({ children }) => {
  const [state, dispatch] = useReducer(rootReducer, initialState);

  // Reference to track the last update time
  const lastUpdateTimeRef = useRef(0);
  const updateIntervalRef = useRef(2000); // Minimum time between updates in ms

  // Effect for terminal display updates
  useEffect(() => {
    // Only update if we have a reader selected, items in cart, and cart is not empty
    // Issue #289: never send the cart while this screen's own payment is in
    // progress. Replacing the reader display cancels the waiting payment.
    // (The server also skips a display while any payment waits on the
    // reader, for other screens.)
    if (
      state.terminal.selectedReader &&
      !state.terminal.isDisplaying &&
      !state.payment.lastOrderCompleted &&
      // No cart display while the payment screen is open (waiting, failed
      // or cancelled): it could replace a payment on the reader or bring
      // back a cart that staff are cancelling. "Back to cart" shows it.
      !state.payment.step &&
      state.cart.items.length > 0 // Only run when cart has items
    ) {
      const now = Date.now();
      const timeSinceLastUpdate = now - lastUpdateTimeRef.current;

      // Only update if enough time has passed since the last update
      if (timeSinceLastUpdate >= updateIntervalRef.current) {
        const displayCart = async () => {
          try {
            // Set displaying flag to prevent multiple simultaneous updates
            dispatch({ type: TERMINAL_SET_DISPLAYING, payload: true });

            // Update the last update time
            lastUpdateTimeRef.current = Date.now();

            await trackDisplay(
              terminalService.displayCart({
                readerId: state.terminal.selectedReader.id,
                cart: state.cart.items,
                totals: state.cart.totals,
                customerName: state.cart.customer?.customerName,
              })
            );

            // Clear any previous errors
            if (state.terminal.error) {
              dispatch({ type: TERMINAL_CLEAR_ERROR });
            }
          } catch (error) {
            dispatch({
              type: TERMINAL_ERROR,
              payload: {
                code: "DISPLAY_FAILED",
                message: error.message || "Failed to display cart on terminal",
              },
            });
          } finally {
            // Reset displaying flag
            dispatch({ type: TERMINAL_SET_DISPLAYING, payload: false });
          }
        };

        // Execute the display cart function
        displayCart();
      } else {
        // If not enough time has passed, schedule an update for later
        const timeToNextUpdate =
          updateIntervalRef.current - timeSinceLastUpdate;
        const timeoutId = setTimeout(() => {
          // This will trigger a re-render which will then execute the effect again
          dispatch({ type: "FORCE_UPDATE" });
        }, timeToNextUpdate);

        return () => clearTimeout(timeoutId);
      }
    }

    // If order was just completed, clear the terminal
    if (state.payment.lastOrderCompleted && state.terminal.selectedReader) {
      const clearTerminal = async () => {
        try {
          // Tracked like a cart display: clearing the reader cancels its
          // current action, so a payment from this screen waits for it.
          await trackDisplay(
            terminalService.clearDisplay(state.terminal.selectedReader.id)
          );
        } catch (error) {}
      };

      clearTerminal();
    }
  }, [
    state.cart.items,
    state.cart.totals,
    state.terminal.selectedReader,
    state.terminal.isDisplaying,
    state.payment.lastOrderCompleted,
    state.payment.step,
    state.cart.customer,
  ]);

  // A cart on the reader: remember it, so it is cleared when the cart is
  // emptied by hand or staff leave the POS page.
  useEffect(() => {
    if (state.cart.items.length > 0) {
      posReaderCart.shown = true;
      return;
    }
    if (state.payment.lastOrderCompleted) {
      // A finished sale clears the reader in the effect above.
      posReaderCart.shown = false;
      return;
    }
    if (
      posReaderCart.shown &&
      state.terminal.selectedReader &&
      state.payment.step !== "processing"
    ) {
      clearReaderCart(state.terminal.selectedReader.id);
    }
  }, [
    state.cart.items.length,
    state.payment.lastOrderCompleted,
    state.payment.step,
    state.terminal.selectedReader,
  ]);

  // Leaving the POS page clears a cart left on the reader: in-app
  // navigation unmounts this provider; closing or reloading the page sends
  // a keepalive clear.
  const selectedReaderRef = useRef(null);
  selectedReaderRef.current = state.terminal.selectedReader;
  useEffect(() => {
    const onPageHide = () => {
      if (posReaderCart.shown && selectedReaderRef.current) {
        posReaderCart.shown = false;
        terminalService.clearDisplayOnUnload(selectedReaderRef.current.id);
      }
    };
    window.addEventListener("pagehide", onPageHide);
    return () => {
      window.removeEventListener("pagehide", onPageHide);
      if (posReaderCart.shown && selectedReaderRef.current) {
        clearReaderCart(selectedReaderRef.current.id);
      }
    };
  }, []);

  // Memoized context value
  const contextValue = React.useMemo(
    () => ({
      state,
      dispatch,
    }),
    [state]
  );

  return (
    <POSContext.Provider value={contextValue}>{children}</POSContext.Provider>
  );
};

// Custom hooks
export const usePOSContext = () => {
  const context = useContext(POSContext);
  if (!context) {
    throw new Error("usePOSContext must be used within a POSProvider");
  }
  return context;
};

export const usePOSCart = () => {
  const { state, dispatch } = usePOSContext();

  const addItem = useCallback(
    (product) => {
      dispatch({ type: CART_ADD_ITEM, payload: product });
    },
    [dispatch]
  );

  const updateQuantity = useCallback(
    (productId, quantity) => {
      dispatch({
        type: CART_UPDATE_QUANTITY,
        payload: { productId, quantity },
      });
    },
    [dispatch]
  );

  const removeItem = useCallback(
    (productId) => {
      dispatch({ type: CART_REMOVE_ITEM, payload: productId });
    },
    [dispatch]
  );

  const clearCart = useCallback(() => {
    dispatch({ type: CART_CLEAR });
  }, [dispatch]);

  const updateItems = useCallback(
    (items) => {
      dispatch({ type: CART_UPDATE_ITEMS, payload: items });
    },
    [dispatch]
  );

  const applyDiscount = useCallback(
    (discount) => {
      dispatch({ type: CART_APPLY_DISCOUNT, payload: discount });
    },
    [dispatch]
  );

  const removeDiscount = useCallback(() => {
    dispatch({ type: CART_REMOVE_DISCOUNT });
  }, [dispatch]);

  const setCustomer = useCallback(
    (customer) => {
      dispatch({ type: CART_SET_CUSTOMER, payload: customer });
    },
    [dispatch]
  );

  const clearCustomer = useCallback(() => {
    dispatch({ type: CART_CLEAR_CUSTOMER });
  }, [dispatch]);

  return {
    items: state.cart.items,
    totals: state.cart.totals,
    appliedDiscount: state.cart.appliedDiscount,
    customer: state.cart.customer,
    addItem,
    updateQuantity,
    removeItem,
    clearCart,
    updateItems,
    applyDiscount,
    removeDiscount,
    setCustomer,
    clearCustomer,
  };
};

export const usePOSTerminal = () => {
  const { state, dispatch } = usePOSContext();

  const setReaders = useCallback(
    (readers) => {
      dispatch({ type: TERMINAL_SET_READERS, payload: readers });
    },
    [dispatch]
  );

  const selectReader = useCallback(
    (reader) => {
      dispatch({ type: TERMINAL_SELECT_READER, payload: reader });
    },
    [dispatch]
  );

  const setLocations = useCallback(
    (locations) => {
      dispatch({ type: TERMINAL_SET_LOCATIONS, payload: locations });
    },
    [dispatch]
  );

  const selectLocation = useCallback(
    (location) => {
      dispatch({ type: TERMINAL_SELECT_LOCATION, payload: location });
    },
    [dispatch]
  );

  const clearError = useCallback(() => {
    dispatch({ type: TERMINAL_CLEAR_ERROR });
  }, [dispatch]);

  const displayCart = useCallback(async () => {
    if (!state.terminal.selectedReader) {
      return { success: false, error: "No reader selected" };
    }

    try {
      dispatch({ type: TERMINAL_SET_DISPLAYING, payload: true });

      const result = await trackDisplay(
        terminalService.displayCart({
          readerId: state.terminal.selectedReader.id,
          cart: state.cart.items,
          totals: state.cart.totals,
          customerName: state.cart.customer?.customerName,
        })
      );

      if (!result.success) {
        dispatch({
          type: TERMINAL_ERROR,
          payload: {
            code: "DISPLAY_FAILED",
            message: result.error || "Failed to display cart on terminal",
          },
        });
      } else {
        dispatch({ type: TERMINAL_CLEAR_ERROR });
      }

      return result;
    } catch (error) {
      dispatch({
        type: TERMINAL_ERROR,
        payload: {
          code: "DISPLAY_FAILED",
          message: error.message || "Failed to display cart on terminal",
        },
      });

      return { success: false, error: error.message };
    } finally {
      dispatch({ type: TERMINAL_SET_DISPLAYING, payload: false });
    }
  }, [
    dispatch,
    state.terminal.selectedReader,
    state.cart.items,
    state.cart.totals,
    state.cart.customer,
  ]);

  const clearTerminalDisplay = useCallback(async () => {
    if (!state.terminal.selectedReader) {
      return { success: false, error: "No reader selected" };
    }

    try {
      const result = await trackDisplay(
        terminalService.clearDisplay(state.terminal.selectedReader.id)
      );
      return result;
    } catch (error) {
      dispatch({
        type: TERMINAL_ERROR,
        payload: {
          code: "CLEAR_FAILED",
          message: error.message || "Failed to clear terminal display",
        },
      });

      return { success: false, error: error.message };
    }
  }, [dispatch, state.terminal.selectedReader]);

  return {
    readers: state.terminal.readers,
    selectedReader: state.terminal.selectedReader,
    locations: state.terminal.locations,
    selectedLocation: state.terminal.selectedLocation,
    isDisplaying: state.terminal.isDisplaying,
    error: state.terminal.error,
    setReaders,
    selectReader,
    setLocations,
    selectLocation,
    clearError,
    displayCart,
    clearTerminalDisplay,
  };
};

export const usePOSPayment = () => {
  const { state, dispatch } = usePOSContext();

  const startPayment = useCallback(() => {
    dispatch({ type: PAYMENT_START });
  }, [dispatch]);

  const setPaymentSuccess = useCallback(() => {
    dispatch({ type: PAYMENT_SUCCESS });
  }, [dispatch]);

  const setPaymentFailure = useCallback(
    (error) => {
      dispatch({ type: PAYMENT_FAILURE, payload: error });
    },
    [dispatch]
  );

  const resetPayment = useCallback(() => {
    dispatch({ type: PAYMENT_RESET });
  }, [dispatch]);

  const setPaymentData = useCallback(
    (data) => {
      dispatch({ type: PAYMENT_SET_DATA, payload: data });
    },
    [dispatch]
  );

  const updatePaymentData = useCallback(
    (data) => {
      dispatch({ type: PAYMENT_UPDATE_DATA, payload: data });
    },
    [dispatch]
  );

  const processPayment = useCallback(
    async (paymentData) => {
      // Kept so "Try again" on the payment screen can repeat this payment.
      posPaymentSession.lastPaymentArgs = paymentData || {};
      if (!state.terminal.selectedReader) {
        dispatch({
          type: PAYMENT_ERROR,
          payload: "Please select a reader to process payment",
        });
        return { success: false, error: "No reader selected" };
      }

      if (state.cart.items.length === 0) {
        dispatch({
          type: PAYMENT_ERROR,
          payload: "Please add items to cart before processing payment",
        });
        return { success: false, error: "Cart is empty" };
      }

      try {
        dispatch({ type: PAYMENT_START });

        // Issue #289: let a cart display in flight finish first, so it
        // cannot clear the reader after this payment is on it.
        if (posDisplayInFlight.promise) {
          await posDisplayInFlight.promise.catch(() => {});
        }

        // Issue #289: stop watching an earlier attempt; the server cancels
        // it before it starts this one.
        stopPaymentWatcher();
        const replacePaymentIntentId = posPaymentSession.openPaymentIntentId;

        // Prepare metadata for the payment
        const metadata = {
          ...(state.cart.appliedDiscount
            ? {
                discount_code: state.cart.appliedDiscount.code,
                discount_amount: state.cart.appliedDiscount.amount,
              }
            : {}),
        };

        // Add customer information to metadata if available
        if (state.cart.customer) {
          metadata.customer_name = state.cart.customer.customerName || "";
          metadata.customer_email = state.cart.customer.customerEmail || "";
          metadata.customer_phone = state.cart.customer.customerPhone || "";
        }

        // Enhance cart items with metadata

        // Ensure each item has complete metadata and uses prorated prices if available
        const enhancedItems = state.cart.items.map((item) => {
          // Check if this is a prorated membership item
          const isProrated = item.isProrated === true;

          // Use prorated price if available
          const price = isProrated ? item.price : item.unitPrice;
          const totalPrice = isProrated
            ? item.price * item.quantity
            : item.totalPrice;

          const resolvedProductType = normalizePOSProductType(
            item.productType ||
              item.metadata?.productType ||
              item.metadata?.product_type ||
              item.metadata?.type ||
              item.metadata?.creditCategory ||
              item.category
          );

          // Make sure each item has its productType properly set without
          // overwriting useful Stripe metadata with a generic fallback.
          const enhancedItem = {
            ...item,
            unitPrice: price, // Use prorated price if available
            totalPrice: totalPrice, // Use prorated total if available
            metadata: {
              ...(item.metadata || {}),
              productType: resolvedProductType || "other",
              totalSessions:
                item.metadata?.totalSessions ||
                item.metadata?.sessionCredits ||
                item.metadata?.sessions ||
                "1",
              // Add proration information if this is a prorated item
              ...(isProrated
                ? {
                    isProrated: "true",
                    originalPrice: item.originalPrice.toString(),
                    proratedPrice: item.price.toString(),
                    nextBillingDate: item.nextBillingDate,
                    billingInterval: item.recurring?.interval || "month",
                    intervalCount: (
                      item.recurring?.interval_count || 1
                    ).toString(),
                  }
                : {}),
            },
          };

          return enhancedItem;
        });

        // Process the payment using terminalService
        posPaymentSession.cancelRequested = false;
        const startCall = terminalService.processPayment({
          readerId: state.terminal.selectedReader.id,
          locationId: state.terminal.selectedLocation.id,
          amount: state.cart.totals.total,
          items: enhancedItems,
          subtotal: state.cart.totals.subtotal,
          taxAmount: state.cart.totals.taxAmount,
          description: generateSmartDescription(
            enhancedItems,
            state.cart.totals
          ),

          customer:
            paymentData.customerId || state.cart.customer?.customerId || null,
          metadata,
          // Issue #289: the server checks the amount against the cart.
          discountAmount: state.cart.totals.discountAmount || 0,
          discountCode: state.cart.appliedDiscount?.code || null,
          replacePaymentIntentId,
        });
        posPaymentSession.starting = startCall;
        let result;
        try {
          result = await startCall;
        } finally {
          if (posPaymentSession.starting === startCall) posPaymentSession.starting = null;
        }

        // On success the server has cancelled the earlier attempt; this
        // one is now open. On failure, keep the earlier attempt (if any) so
        // the next try still cancels it; a new attempt that failed at the
        // reader was already cancelled by the server.
        posPaymentSession.openPaymentIntentId = result.success
          ? result.paymentIntentId
          : replacePaymentIntentId;

        // Staff pressed "Cancel payment" while this payment was starting:
        // the cancel (waiting on this call) cancels it on the server and
        // shows the result. Do not watch it.
        if (posPaymentSession.cancelRequested) {
          posPaymentSession.cancelRequested = false;
          return { success: false, status: "canceled" };
        }

        if (!result.success) {
          if (result.needsRegistration) {
            dispatch({
              type: PAYMENT_ERROR,
              payload:
                'Reader needs to be registered before processing payments. Please use the "Register New Reader" button.',
            });
          } else if (
            result.error &&
            result.error.includes("Invalid customer ID format")
          ) {
            dispatch({
              type: PAYMENT_ERROR,
              payload:
                "Selected customer does not have a valid Stripe account. Please contact support to resolve this issue.",
            });
          } else if (
            result.error &&
            result.error.includes("Customer not found in Stripe")
          ) {
            dispatch({
              type: PAYMENT_ERROR,
              payload:
                "Selected customer's Stripe account could not be found. Please contact support to resolve this issue.",
            });
          } else {
            dispatch({
              type: PAYMENT_ERROR,
              payload:
                result.error || "Payment processing failed. Please try again.",
            });
          }

          dispatch({ type: PAYMENT_ERROR_CODE, payload: result.code || null });
          dispatch({ type: PAYMENT_FAILURE, payload: result.error });
          return result;
        }

        // Store payment data
        const paymentIntentId = result.paymentIntentId;
        dispatch({
          type: PAYMENT_SET_DATA,
          payload: {
            clientSecret: result.clientSecret,
            paymentIntentId: paymentIntentId,
            transactionId: result.transactionId,
            customerEmail:
              paymentData.customerEmail ||
              state.cart.customer?.customerEmail ||
              null,
          },
        });

        // Watch the payment until it has one clear state (issue #289).
        posPaymentSession.watcher = terminalService.startPaymentStatusPolling(
          paymentIntentId,
          state.cart.totals.total,
          (result) => {
            posPaymentSession.watcher = null;
            // Paid or cancelled: nothing is left open. A failed attempt
            // stays open on Stripe's side; the next try cancels it.
            if (result.success || result.status === "canceled") {
              posPaymentSession.openPaymentIntentId = null;
            }
            if (result.success) {
              // Payment was successful, update the state

              // Check if any items require a payment method
              const requiresPaymentMethod =
                result.data?.requiresPaymentMethod ||
                result.data?.transaction?.requiresPaymentMethod ||
                false;

              if (requiresPaymentMethod) {
                // Update payment data with requiresPaymentMethod flag
                dispatch({
                  type: PAYMENT_UPDATE_DATA,
                  payload: {
                    requiresPaymentMethod: true,
                    customerId:
                      state.cart.customer?.customerId ||
                      result.data?.transaction?.stripeCustomerId,
                    transaction: result.data?.transaction,
                    // Include other relevant data
                    ...result.data,
                  },
                });
              } else {
                // Update payment data with any additional info from the result
                if (result.data && result.data.transaction) {
                  // Include process results if available to get subscription details
                  const processResults = result.data.processResults || [];

                  dispatch({
                    type: PAYMENT_UPDATE_DATA,
                    payload: {
                      cardBrand: result.data.transaction.cardBrand,
                      last4: result.data.transaction.last4,
                      captureDetails: {
                        ...result.data,
                        processResults,
                      },
                    },
                  });
                }
              }
              // Set payment success state
              dispatch({ type: PAYMENT_SUCCESS });
            } else {
              // Payment failed, update the state
              dispatch({
                type: PAYMENT_FAILURE,
                payload: result.error || "Payment processing failed",
              });
            }
          }
        );

        return result;
      } catch (error) {
        dispatch({
          type: PAYMENT_ERROR,
          payload:
            error.message || "Payment processing failed. Please try again.",
        });

        dispatch({ type: PAYMENT_FAILURE, payload: error.message });

        return { success: false, error: error.message };
      }
    },
    [
      dispatch,
      state.terminal.selectedReader,
      state.terminal.selectedLocation,
      state.cart.items,
      state.cart.totals,
      state.cart.appliedDiscount,
      state.cart.customer,
    ]
  );

  // Issue #289 — staff cancel the payment that is waiting on the reader.
  // One clear result: cancelled; or, if the card went through at the same
  // moment, paid (the server delivers it once).
  const cancelPayment = useCallback(async () => {
    stopPaymentWatcher();
    // A payment still starting has no ID yet, and the ID kept from an
    // earlier attempt (for example a decline before "Try again") is not
    // the one on the reader. Wait for the start, then cancel the payment
    // it opened, so it does not stay live on the reader.
    if (posPaymentSession.starting) {
      posPaymentSession.cancelRequested = true;
      await posPaymentSession.starting.catch(() => {});
    }
    const paymentIntentId =
      posPaymentSession.openPaymentIntentId ||
      state.payment.paymentData?.paymentIntentId;
    if (!paymentIntentId) {
      dispatch({ type: PAYMENT_FAILURE, payload: "The payment was cancelled." });
      dispatch({ type: PAYMENT_ERROR_CODE, payload: STAFF_CANCELLED });
      return { status: "canceled" };
    }
    try {
      await terminalAPI.cancelPaymentIntent(
        paymentIntentId,
        "staff_change_payment_method"
      );
      posPaymentSession.openPaymentIntentId = null;
      dispatch({ type: PAYMENT_FAILURE, payload: "The payment was cancelled." });
      dispatch({ type: PAYMENT_ERROR_CODE, payload: STAFF_CANCELLED });
      return { status: "canceled" };
    } catch (error) {
      try {
        const { data } = await terminalAPI.getPosPaymentStatus(paymentIntentId);
        if (data.status === "succeeded") {
          posPaymentSession.openPaymentIntentId = null;
          dispatch({
            type: PAYMENT_UPDATE_DATA,
            payload: {
              transaction: data.transaction,
              requiresPaymentMethod: Boolean(data.requiresPaymentMethod),
            },
          });
          dispatch({ type: PAYMENT_SUCCESS });
          return { status: "succeeded" };
        }
      } catch (statusError) {
        // fall through to the cancel error below
      }
      const message =
        error?.response?.data?.error ||
        "The payment could not be cancelled. Check the reader, then try again.";
      dispatch({ type: PAYMENT_FAILURE, payload: message });
      return { status: "error", error: message };
    }
  }, [dispatch, state.payment.paymentData]);

  // Test mode only (staging): tap a test card on the simulated reader.
  const simulateTap = useCallback(async () => {
    const paymentIntentId =
      posPaymentSession.openPaymentIntentId ||
      state.payment.paymentData?.paymentIntentId;
    if (!paymentIntentId) return;
    try {
      await terminalAPI.simulatePosTap(paymentIntentId);
    } catch (error) {
      // The status watcher reports the result.
    }
  }, [state.payment.paymentData]);

  // "Try again" on the payment screen: the same payment again (same
  // customer and discount). processPayment cancels the previous attempt.
  const retryPayment = useCallback(
    () => processPayment(posPaymentSession.lastPaymentArgs || {}),
    [processPayment]
  );

  // "Cancel sale": end the sale without a charge. Empties the cart, the
  // customer and the discount, closes the payment screen, and clears the
  // cart from the reader (the server never cancels a waiting payment).
  const cancelSale = useCallback(async () => {
    stopPaymentWatcher();
    posPaymentSession.lastPaymentArgs = null;
    // Cleared below; stops the "cart emptied" effect clearing it twice.
    posReaderCart.shown = false;
    dispatch({ type: CART_CLEAR });
    dispatch({ type: CART_CLEAR_CUSTOMER });
    dispatch({ type: CART_REMOVE_DISCOUNT });
    dispatch({ type: PAYMENT_RESET });
    const readerId = state.terminal.selectedReader?.id;
    if (!readerId) return { success: true };
    return clearReaderCart(readerId);
  }, [dispatch, state.terminal.selectedReader]);

  // "Clear reader" (issue #289): frees the selected reader when a sale is
  // refused because another payment is waiting on it (for example an old
  // attempt left after a page reload). The server cancels the reader
  // action, and an unpaid attempt on it; a paid one is never cancelled.
  const clearReader = useCallback(async () => {
    const readerId = state.terminal.selectedReader?.id;
    if (!readerId) return { success: false, error: "No reader selected" };
    try {
      const { data } = await terminalAPI.clearPosReader(readerId);
      dispatch({ type: PAYMENT_ERROR_CODE, payload: null });
      return { success: true, message: data?.message, result: data?.result };
    } catch (error) {
      return {
        success: false,
        code: error?.response?.data?.code,
        error: error?.response?.data?.error || "Could not clear the card reader. Try again.",
      };
    }
  }, [dispatch, state.terminal.selectedReader]);

  return {
    step: state.payment.step,
    paymentData: state.payment.paymentData,
    error: state.payment.error,
    errorCode: state.payment.errorCode,
    clearReader,
    lastOrderCompleted: state.payment.lastOrderCompleted,
    startPayment,
    setPaymentSuccess,
    setPaymentFailure,
    resetPayment,
    setPaymentData,
    updatePaymentData,
    processPayment,
    cancelPayment,
    simulateTap,
    retryPayment,
    cancelSale,
  };
};

export default POSContext;
